import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  refreshPreview,
  refreshTranslation,
  setInstruction,
  translationStyle,
  type Book,
  type Effort,
} from "../api";
import { Amount } from "../components/Amount";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import EffortChips from "./EffortChips";
import SamplePanel from "./SamplePanel";
import { useTranslation } from "./TranslationBanner";
import { useGrowToText } from "./useGrowToText";

/**
 * LinkedInstruction is how a linked book is translated, on its settings screen
 * (requirement 0041, ADR-0096; requirement 0042, ADR-0097): the instruction it
 * was made with, how hard the model is asked to think, Try it against the
 * **source** — and the Refresh that applies all of it.
 *
 * **A control that destroys work is never fused with the control that
 * configures it — one press, not one screen.** Saving and refreshing sit
 * together here and stay two presses, and the warning about lost audio belongs
 * beside Refresh, because that is the press that destroys it (ADR-0096 §4 and
 * its *Since*).
 *
 * **The effort is an argument to both presses**, which is why it is here rather
 * than beside one of them: a sample taken at a different effort from the run it
 * predicts is worse than no sample (ADR-0097 §4). It is also deliberately not
 * an input to any digest, so changing it re-translates nothing by itself — and
 * the note under the chips says so, or somebody turns the knob to High, presses
 * Refresh, and gets a run that makes no calls at all.
 *
 * Nothing for a book that is not linked: there is no refresh for an instruction
 * to affect.
 */
export default function LinkedInstruction({ book }: { book: Book }) {
  const t = useT();
  const client = useQueryClient();
  const [typed, setTyped] = useState<string | null>(null);
  const [preset, setPreset] = useState("");
  const [effort, setEffort] = useState<Effort | null>(null);

  const style = useQuery({
    // `only=language` filters the presets to this book's own: a preset carries a
    // language and the destination was settled at Start, so offering one in
    // another language is offering a different book (ADR-0096 §6).
    queryKey: ["translation-style", book.language, "own"],
    queryFn: () => translationStyle(book.language, true),
    enabled: !!book.linked_source_id,
  });
  // The instruction the book is translated with today is the run's own state.
  const run = useTranslation(book.id);
  const current = run.data?.instruction ?? style.data?.default_instruction ?? "";

  // What the chip sits on: the author's choice, else what this book's last pass
  // actually ran at, else the server's default. Seeding from the run is why the
  // run records a defaulted effort rather than leaving it empty (ADR-0097 §1).
  const chosen: Effort = effort ?? run.data?.effort ?? style.data?.default_effort ?? "medium";

  // Keyed by the effort, so the estimate on the button is the price of the press
  // the chip is currently sitting on (ADR-0097 §6).
  const preview = useQuery({
    queryKey: ["refresh-preview", book.id, chosen],
    queryFn: () => refreshPreview(book.id, chosen),
    enabled: !!book.linked_source_id,
  });

  const save = useMutation({
    mutationFn: () => setInstruction(book.id, shown, preset, chosen),
    onSuccess: (next) => {
      client.setQueryData(["refresh-preview", book.id, chosen], next);
      void client.invalidateQueries({ queryKey: ["refresh-preview", book.id] });
      void client.invalidateQueries({ queryKey: ["translation", book.id] });
      setTyped(null);
      setPreset("");
    },
  });
  const refresh = useMutation({
    mutationFn: () => refreshTranslation(book.id, chosen),
    onSuccess: (started) => {
      client.setQueryData(["translation", book.id], started);
      void client.invalidateQueries({ queryKey: ["refresh-preview", book.id] });
    },
  });

  const presets = style.data?.presets ?? [];
  const chooseable = style.data?.efforts ?? ["fast", "medium", "high"];
  const chosenPreset = presets.find((p) => p.id === preset);
  // What is on screen: the author's edit, or a chosen preset's words, or what
  // the book is actually translated with today.
  const shown = typed ?? chosenPreset?.instruction ?? current;
  const instructionBox = useGrowToText(shown);

  if (!book.linked_source_id) return null;
  const changes = preview.data;
  const dirty = shown !== current;
  // A run in flight refuses both presses, the same rule the server answers.
  const busy = run.data?.status === "running" || run.data?.status === "paused";

  return (
    <>
      <section data-testid="linked-instruction">
        <h2 className="text-sm font-medium">{t.translation.howHeading}</h2>
        <p className="mt-1 mb-2 text-xs text-muted">{t.translation.howLinkedNote}</p>

        {presets.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`linked-preset-${p.id}`}
                aria-pressed={p.id === preset}
                onClick={() => {
                  setPreset(p.id === preset ? "" : p.id);
                  setTyped(null);
                }}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  p.id === preset
                    ? "border-sage bg-sage-soft font-medium"
                    : "border-line hover:bg-sunk"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        <label className="sr-only" htmlFor="linked-instruction-text">
          {t.translation.howHeading}
        </label>
        <textarea
          id="linked-instruction-text"
          data-testid="linked-instruction-text"
          dir="auto"
          rows={3}
          ref={instructionBox}
          maxLength={style.data?.max_runes}
          value={shown}
          onChange={(e) => {
            setTyped(e.target.value);
          }}
          className="w-full resize-none overflow-hidden rounded-control border border-line bg-surface px-3 py-2 text-sm leading-relaxed"
        />

        <div className="mt-4">
          <h3 className="text-sm font-medium">{t.translation.effortHeading}</h3>
          <p className="mt-1 mb-2 text-xs text-muted">{t.translation.effortNote}</p>
          <EffortChips
            value={chosen}
            offered={chooseable}
            onChange={setEffort}
            disabled={busy}
            testIdPrefix="linked-effort"
          />
          <p className="mt-2 text-xs text-muted">{t.translation.effortLinkedNote}</p>
        </div>

        {/* Try it and Another paragraph, against the source — which is where
            the words come from. Save sits in the same row because all three are
            what an author does to the instruction above (ADR-0099). */}
        <SamplePanel
          testID="linked-instruction"
          order={{
            bookID: book.linked_source_id,
            language: book.language,
            instruction: shown,
            effort: chosen,
          }}
          limits={false}
          note={t.translation.howSaveNote}
        >
          <button
            type="button"
            data-testid="linked-instruction-save"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-full bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {t.translation.howSave}
          </button>
        </SamplePanel>

        <MutationError
          className="mt-2"
          testId="linked-instruction-error"
          error={save.error}
          fallback={t.translation.howSaveFailed}
        />
      </section>

      {/* The press that spends and destroys, its own section and its own
          decision — moved here from the book's own screen, which is a list of
          chapters and no place for a control that re-translates the whole book
          (ADR-0096 *Since*). */}
      <section data-testid="linked-refresh-panel">
        <h2 className="text-sm font-medium">{t.translation.refreshHeading}</h2>

        <div data-testid="linked-changes" className="mt-2 text-xs text-muted">
          {preview.isPending ? (
            t.translation.checking
          ) : changes && !changes.any ? (
            t.translation.upToDate
          ) : changes ? (
            <ul className="list-inside list-disc space-y-0.5">
              {changes.details && <li>{t.translation.changeDetails}</li>}
              {changes.chapters_changed > 0 && (
                <li>{t.translation.changeChapters(changes.chapters_changed)}</li>
              )}
              {changes.chapters_added > 0 && (
                <li>{t.translation.changeAdded(changes.chapters_added)}</li>
              )}
              {changes.chapters_removed > 0 && (
                <li>{t.translation.changeRemoved(changes.chapters_removed)}</li>
              )}
              {changes.pictures_changed > 0 && (
                <li>{t.translation.changePictures(changes.pictures_changed)}</li>
              )}
            </ul>
          ) : null}
        </div>

        {/* One warning, beside the press that does it. The cause first, when the
            server knows one: "you changed the instruction" is the truth, and the
            counts above would otherwise read as a rewritten source. */}
        {changes?.any &&
          (changes.cause || changes.audio_outdated > 0 || changes.chapters_edited > 0) && (
            <div
              data-testid="linked-destroys"
              role="status"
              className="mt-2 rounded-control border border-amber-300 bg-amber-50 p-2.5 text-xs dark:border-amber-800 dark:bg-amber-950/40"
            >
              {changes.cause === "instruction" && <p>{t.translation.causeInstruction}</p>}
              {changes.audio_outdated > 0 && (
                <p className="mt-1 font-medium">
                  {t.translation.audioOutdated(changes.audio_outdated)}
                </p>
              )}
              {/* What the author stands to have re-translated under them. Their
                wording is kept either way (ADR-0098 §5) — this says so, so the
                warning reads as a heads-up and not a threat. */}
              {changes.chapters_edited > 0 && (
                <p data-testid="linked-edited" className="mt-1">
                  {t.translation.chaptersEdited(changes.chapters_edited)}
                </p>
              )}
            </div>
          )}

        {changes?.any && (
          <button
            type="button"
            data-testid="linked-refresh"
            disabled={refresh.isPending || busy}
            onClick={() => refresh.mutate()}
            className="mt-3 rounded-full bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-50"
          >
            {refresh.isPending ? (
              t.translation.refreshing
            ) : changes.estimate_micros > 0 ? (
              <Amount sentence={t.translation.refreshWithCost} amount={changes.estimate} />
            ) : (
              t.translation.refresh
            )}
          </button>
        )}

        <MutationError
          className="mt-2"
          testId="linked-refresh-error"
          error={refresh.error}
          fallback={t.translation.refreshFailed}
        />
      </section>
    </>
  );
}
